import { Loader, Modal, PasswordInput, TextInput } from '@mantine/core';
import { IconKey } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { Endpoints } from '@xver/apis';
import type { GetCredentialResponse } from '@xver/apis/v1/kufer/registry-credentials/model';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../../../components/Atoms';
import { useCredentialMutations } from '../hooks/useCredentialMutations';

interface CredentialFormModalProps {
    mode: 'create' | 'edit';
    opened: boolean;
    credentialId?: string;
    onClose: () => void;
    onSuccess?: (id: string) => void;
}

const INPUT_STYLES = {
    input: {
        backgroundColor: 'var(--surface-2)',
        color: 'var(--fg)',
    },
    label: { color: 'color-mix(in srgb, var(--fg) 85%, transparent)', marginBottom: '0.25rem' },
    description: { color: 'color-mix(in srgb, var(--fg) 68%, transparent)' },
    innerInput: { color: 'var(--fg)' },
};

export default function CredentialFormModal({
    mode,
    opened,
    credentialId,
    onClose,
    onSuccess,
}: CredentialFormModalProps) {
    const { t } = useTranslation('containerEngine');
    const { createMutation, updateMutation } = useCredentialMutations();

    const [name, setName] = useState('');
    const [endpoint, setEndpoint] = useState('docker.io');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');

    const isEdit = mode === 'edit';
    const isPending = createMutation.isPending || updateMutation.isPending;

    const { data: existing, isFetching: isLoadingExisting } = useQuery<GetCredentialResponse>({
        queryKey: ['apis.v1.kufer.registry-credentials.get', credentialId],
        queryFn: () => Endpoints.v1.Kufer.RegistryCredentials.getCredential(credentialId as string),
        enabled: isEdit && opened && !!credentialId,
    });

    useEffect(() => {
        if (!opened) {
            setName('');
            setEndpoint('docker.io');
            setUsername('');
            setPassword('');
            return;
        }
        if (isEdit && existing) {
            setName(existing.name);
            setEndpoint(existing.endpoint || 'docker.io');
            setUsername(existing.username);
            setPassword('');
        }
    }, [opened, isEdit, existing]);

    const handleClose = () => {
        if (isPending) return;
        onClose();
    };

    const handleSubmit = () => {
        if (isEdit && credentialId) {
            updateMutation.mutate(
                {
                    id: credentialId,
                    data: {
                        name,
                        endpoint: endpoint.trim(),
                        username,
                        password: password ? password : undefined,
                    },
                },
                {
                    onSuccess: () => {
                        onSuccess?.(credentialId);
                        onClose();
                    },
                }
            );
            return;
        }

        createMutation.mutate(
            { name, endpoint: endpoint.trim(), username, password },
            {
                onSuccess: (data) => {
                    onSuccess?.(data.id);
                    onClose();
                },
            }
        );
    };

    const canSubmit =
        name.trim().length > 0 &&
        endpoint.trim().length > 0 &&
        username.trim().length > 0 &&
        (isEdit || password.length > 0);

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            centered
            size="md"
            closeOnClickOutside={!isPending}
            closeOnEscape={!isPending}
            title={
                <div className="inline-flex items-center gap-2">
                    <IconKey size={20} />
                    <span className="font-semibold">
                        {isEdit
                            ? t('credentialForm.editTitle', 'Edit registry credential')
                            : t('credentialForm.createTitle', 'New registry credential')}
                    </span>
                </div>
            }
        >
            {isEdit && isLoadingExisting ? (
                <div className="w-full flex justify-center py-6">
                    <Loader size={20} color="var(--fg)" />
                </div>
            ) : (
                <div className="w-full flex flex-col gap-4">
                    <TextInput
                        size="md"
                        label={t('credentialForm.name', 'Name')}
                        placeholder={t('credentialForm.namePlaceholder', 'e.g. dockerhub-personal')}
                        value={name}
                        onChange={(e) => setName(e.currentTarget.value)}
                        styles={INPUT_STYLES}
                        required
                    />
                    <TextInput
                        size="md"
                        label={t('credentialForm.endpoint', 'Registry endpoint')}
                        placeholder={t('credentialForm.endpointPlaceholder', 'docker.io')}
                        description={t(
                            'credentialForm.endpointHint',
                            'Registry host, e.g. docker.io, ghcr.io, registry.gitlab.com'
                        )}
                        value={endpoint}
                        onChange={(e) => setEndpoint(e.currentTarget.value)}
                        styles={INPUT_STYLES}
                        required
                    />
                    <TextInput
                        size="md"
                        label={t('credentialForm.username', 'Username')}
                        placeholder={t('credentialForm.usernamePlaceholder', 'docker-user')}
                        value={username}
                        onChange={(e) => setUsername(e.currentTarget.value)}
                        styles={INPUT_STYLES}
                        required
                    />
                    <PasswordInput
                        size="md"
                        label={t('credentialForm.password', 'Password / token')}
                        placeholder={
                            isEdit
                                ? t(
                                      'credentialForm.passwordEditHint',
                                      'Leave blank to keep current'
                                  )
                                : t('credentialForm.passwordPlaceholder', '••••••••')
                        }
                        value={password}
                        onChange={(e) => setPassword(e.currentTarget.value)}
                        styles={INPUT_STYLES}
                        required={!isEdit}
                    />

                    <div className="w-full flex flex-row justify-end items-center gap-2 mt-2">
                        <Button variant="secondary" onClick={handleClose} disabled={isPending}>
                            {t('credentialForm.cancel', 'Cancel')}
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            disabled={!canSubmit || isPending}
                            isLoading={isPending}
                        >
                            {isEdit
                                ? t('credentialForm.save', 'Save changes')
                                : t('credentialForm.create', 'Create credential')}
                        </Button>
                    </div>
                </div>
            )}
        </Modal>
    );
}
